import React, { useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'

export interface DropdownOption {
  value: string
  label: string
  group?: string
  disabled?: boolean
}

interface CustomDropdownProps {
  id: string
  label?: string
  placeholder?: string
  options: DropdownOption[]
  selectedValues: string[]
  onSelectionChange: (values: string[]) => void
  multiSelect?: boolean
  maxSelections?: number
  minWidth?: string
  disabled?: boolean
  showClearAll?: boolean
  clearAllText?: string
  renderSelectedValue?: (value: string, option: DropdownOption) => React.ReactNode
  onDropdownToggle?: (isOpen: boolean) => void
  transparency?: 'low' | 'medium' | 'high' | 'none'
  searchable?: boolean
  searchPlaceholder?: string
  rounded?: boolean
  showGroups?: boolean
}

export default function CustomDropdown({
  id,
  label,
  placeholder = "Select options",
  options,
  selectedValues,
  onSelectionChange,
  multiSelect = false,
  maxSelections,
  minWidth = "min-w-[200px]",
  disabled = false,
  showClearAll = false,
  clearAllText = "Clear All",
  renderSelectedValue,
  onDropdownToggle,
  transparency = 'medium',
  searchable = false,
  searchPlaceholder = "Search...",
  rounded = true,
  showGroups = false
}: CustomDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState('')
  const [dropdownPosition, setDropdownPosition] = React.useState({ top: 0, left: 0, width: 0 })

  const handleToggle = () => {
    if (disabled) return
    const newIsOpen = !isOpen
    setIsOpen(newIsOpen)
    
    if (newIsOpen && dropdownRef.current) {
      // Calculate position for portal rendering
      const rect = dropdownRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      })
    }
    
    if (!newIsOpen) {
      setSearchTerm('') // Clear search when closing
    }
    onDropdownToggle?.(newIsOpen)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation()
    setSearchTerm(e.target.value)
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
      setSearchTerm('')
      onDropdownToggle?.(false)
    } else if (e.key === 'Enter' && filteredOptions.length > 0) {
      // Select the first filtered option on Enter
      const firstOption = filteredOptions[0]
      if (!isOptionDisabled(firstOption)) {
        handleOptionClick(firstOption.value)
        if (!multiSelect) {
          setIsOpen(false)
          setSearchTerm('')
          onDropdownToggle?.(false)
        }
      }
    } else if (e.key === 'ArrowDown' && filteredOptions.length > 0) {
      // Allow arrow key navigation to first option
      e.preventDefault()
      const firstOption = filteredOptions[0]
      if (!isOptionDisabled(firstOption)) {
        // Highlight the first option (we could add visual feedback here)
      }
    }
  }

  const handleMainAreaClick = (e: React.MouseEvent) => {
    if (disabled) return
    if (searchable) {
      e.stopPropagation()
      setIsOpen(true)
      onDropdownToggle?.(true)
      
      // Calculate position for portal rendering when opening
      if (dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect()
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width
        })
      }
      
      // Focus will be handled by the input's autoFocus prop
    } else {
      handleToggle()
    }
  }

  const handleMainAreaKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return
    if (searchable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      e.stopPropagation()
      setIsOpen(true)
      onDropdownToggle?.(true)
      
      // Calculate position for portal rendering when opening
      if (dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect()
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width
        })
      }
    }
  }

  const filteredOptions = searchable && searchTerm
    ? options.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options

  const handleOptionClick = (value: string) => {
    if (disabled) return

    if (multiSelect) {
      if (selectedValues.includes(value)) {
        // Remove value
        onSelectionChange(selectedValues.filter(v => v !== value))
      } else {
        // Add value (check max limit)
        if (!maxSelections || selectedValues.length < maxSelections) {
          onSelectionChange([...selectedValues, value])
        }
      }
    } else {
      // Single select
      onSelectionChange([value])
      setIsOpen(false)
    }
  }

  const handleClearAll = () => {
    onSelectionChange([])
  }

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node
    
    // Check if click is within the main dropdown trigger
    const isWithinMainDropdown = dropdownRef.current && dropdownRef.current.contains(target)
    
    // Check if click is within the portal-rendered dropdown content
    const dropdownContent = document.getElementById(id)
    const isWithinDropdownContent = dropdownContent && dropdownContent.contains(target)
    
    // Only close if click is outside both the main dropdown and the dropdown content
    if (!isWithinMainDropdown && !isWithinDropdownContent) {
      setIsOpen(false)
      setSearchTerm('') // Reset search term when clicking outside
      onDropdownToggle?.(false)
    }
  }, [onDropdownToggle, id])

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, handleClickOutside])



  const getSelectedDisplay = () => {
    // Show search input in main area when searchable and dropdown is open
    if (searchable && isOpen) {
      return (
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          onClick={(e) => e.stopPropagation()}
          autoFocus
          className="w-full bg-transparent text-sm text-gray-900 outline-none"
        />
      )
    }

    if (selectedValues.length === 0) {
      return <span className="text-gray-500 text-sm">{placeholder}</span>
    }

    if (multiSelect) {
      return (
        <div className="flex flex-wrap gap-1 pr-6">
          {selectedValues.map(value => {
            const option = options.find(opt => opt.value === value)
            if (!option) return null

            if (renderSelectedValue) {
              return renderSelectedValue(value, option)
            }

            return (
              <span
                key={value}
                className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800 max-w-[120px]"
                title={option.label}
              >
                <span className="truncate flex-1 min-w-0 mr-1">{option.label}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleOptionClick(value)
                  }}
                  className="inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none flex-shrink-0"
                >
                  <span className="sr-only">Remove {option.label}</span>
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </span>
            )
          })}
        </div>
      )
    } else {
      // Single select
      const selectedOption = options.find(opt => opt.value === selectedValues[0])
      if (!selectedOption) return <span className="text-gray-500 text-sm">{placeholder}</span>

      if (renderSelectedValue) {
        return renderSelectedValue(selectedValues[0], selectedOption)
      }

      return (
        <span className="text-sm text-gray-900 truncate block w-full pr-6" title={selectedOption.label}>
          {selectedOption.label}
        </span>
      )
    }
  }

  const isOptionDisabled = (option: DropdownOption) => {
    if (disabled) return true
    if (option.disabled) return true
    if (!multiSelect) return false
    if (!maxSelections) return false
    return !selectedValues.includes(option.value) && selectedValues.length >= maxSelections
  }

  // Group options by their group property
  const groupedOptions = showGroups ? (() => {
    const groups: { [key: string]: DropdownOption[] } = {}
    
    filteredOptions.forEach(option => {
      const group = option.group || 'Ungrouped'
      if (!groups[group]) {
        groups[group] = []
      }
      groups[group].push(option)
    })
    
    return Object.entries(groups).map(([groupName, groupOptions]) => ({
      groupName,
      options: groupOptions
    }))
  })() : null



  const renderDropdownContent = () => (
    <div 
      id={id}
      className={`${isOpen ? '' : 'hidden'} relative w-full ${
        transparency === 'none' 
          ? 'bg-white border border-gray-200' 
          : transparency === 'low'
          ? 'bg-gradient-to-b from-white/90 to-white/85 backdrop-blur-sm border border-gray-200/70'
          : transparency === 'high'
          ? 'bg-gradient-to-b from-white/30 to-white/20 backdrop-blur-lg border border-gray-200/30'
          : 'bg-gradient-to-b from-white/70 to-white/60 backdrop-blur-md border border-gray-200/50'
      } ${rounded ? 'rounded-md' : ''} shadow-xl max-h-60 overflow-auto`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="p-2 w-full">

        {showClearAll && multiSelect && selectedValues.length > 0 && (
          <div className="flex items-center justify-end mb-2">
            <button
              type="button"
              onClick={handleClearAll}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {clearAllText}
            </button>
          </div>
        )}
        
        {maxSelections && multiSelect && selectedValues.length >= maxSelections && (
          <div className="px-2 py-1 text-xs text-amber-600 bg-gradient-to-r from-amber-50/70 to-amber-100/50 rounded mb-2 backdrop-blur-sm">
            Maximum {maxSelections} options selected
          </div>
        )}

        {filteredOptions.length === 0 && searchable && searchTerm && (
          <div className="px-2 py-2 text-sm text-gray-500 text-center">
            No options found for "{searchTerm}"
          </div>
        )}
        
        <div className="space-y-1">
          {showGroups && groupedOptions ? (
            // Render grouped options
            groupedOptions.map(({ groupName, options }) => (
              <div key={groupName}>
                {groupName !== 'Ungrouped' && (
                  <div className="px-2 py-1 text-xs font-medium text-gray-500 bg-gray-50 rounded-t">
                    {groupName}
                  </div>
                )}
                {options.map(option => {
                  const isSelected = selectedValues.includes(option.value)
                  const isDisabled = isOptionDisabled(option)

                  return (
                    <label
                      key={option.value}
                      className={`flex items-center p-2 rounded cursor-pointer transition-all duration-200 ease-out ${
                        groupName !== 'Ungrouped' ? 'ml-2' : ''
                      } ${
                        isDisabled
                          ? 'opacity-50 cursor-not-allowed'
                          : isSelected
                          ? transparency === 'none'
                            ? 'bg-blue-50'
                            : transparency === 'low'
                            ? 'bg-gradient-to-r from-blue-50/90 to-blue-100/80 backdrop-blur-sm'
                            : transparency === 'high'
                            ? 'bg-gradient-to-r from-blue-50/40 to-blue-100/30 backdrop-blur-lg'
                            : 'bg-gradient-to-r from-blue-50/70 to-blue-100/50 backdrop-blur-sm'
                          : transparency === 'none'
                          ? 'hover:bg-gray-50'
                          : transparency === 'low'
                          ? 'hover:bg-gradient-to-r hover:from-gray-50/90 hover:to-gray-100/80 hover:backdrop-blur-sm'
                          : transparency === 'high'
                          ? 'hover:bg-gradient-to-r hover:from-gray-50/40 hover:to-gray-100/30 hover:backdrop-blur-lg'
                          : 'hover:bg-gradient-to-r hover:from-gray-50/70 hover:to-gray-100/60 hover:backdrop-blur-sm'
                      }`}
                      onClick={() => !isDisabled && handleOptionClick(option.value)}
                    >
                      <input
                        type="checkbox"
                        name={multiSelect ? undefined : id}
                        value={option.value}
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => handleOptionClick(option.value)}
                        className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0"
                      />
                      <span 
                        className={`text-sm ${isSelected ? 'text-blue-800 font-semibold' : 'text-gray-900'} truncate flex-1 min-w-0`}
                        title={option.label}
                      >
                        {option.label}
                      </span>
                    </label>
                  )
                })}
              </div>
            ))
          ) : (
            // Render regular options
            filteredOptions.map(option => {
              const isSelected = selectedValues.includes(option.value)
              const isDisabled = isOptionDisabled(option)
              
              return (
                <label 
                  key={option.value} 
                  className={`flex items-center p-2 rounded cursor-pointer transition-all duration-200 ease-out ${
                    isDisabled
                      ? 'opacity-50 cursor-not-allowed'
                      : isSelected
                      ? transparency === 'none'
                        ? 'bg-blue-50'
                        : transparency === 'low'
                        ? 'bg-gradient-to-r from-blue-50/90 to-blue-100/80 backdrop-blur-sm'
                        : transparency === 'high'
                        ? 'bg-gradient-to-r from-blue-50/40 to-blue-100/30 backdrop-blur-lg'
                        : 'bg-gradient-to-r from-blue-50/70 to-blue-100/50 backdrop-blur-sm'
                      : transparency === 'none'
                      ? 'hover:bg-gray-50'
                      : transparency === 'low'
                      ? 'hover:bg-gradient-to-r hover:from-gray-50/90 hover:to-gray-100/80 hover:backdrop-blur-sm'
                      : transparency === 'high'
                      ? 'hover:bg-gradient-to-r hover:from-gray-50/40 hover:to-gray-100/30 hover:backdrop-blur-lg'
                      : 'hover:bg-gradient-to-r hover:from-gray-50/70 hover:to-gray-100/60 hover:backdrop-blur-sm'
                  }`}
                >
                  <input
                    type="checkbox"
                    name={multiSelect ? undefined : id}
                    value={option.value}
                    checked={isSelected}
                    disabled={isDisabled}
                    onChange={() => handleOptionClick(option.value)}
                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0"
                  />
                  <span 
                    className={`text-sm ${isSelected ? 'text-blue-800 font-semibold' : 'text-gray-900'} truncate flex-1 min-w-0`}
                    title={option.label}
                  >
                    {option.label}
                  </span>
                </label>
              )
            })
          )}
        </div>
      </div>
    </div>
  )

  return (
    <div id={`${id}-filter`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <div 
          className={`min-h-[42px] ${minWidth} border border-gray-300 ${rounded ? 'rounded-md' : ''} focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 p-2 relative ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : 'cursor-pointer'
          } bg-white/95 backdrop-blur-sm`}
          onClick={handleMainAreaClick}
          onKeyDown={handleMainAreaKeyDown}
          tabIndex={disabled ? -1 : 0}
          role="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          {getSelectedDisplay()}
          
          {/* Dropdown arrow button */}
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <button
              type="button"
              onClick={handleToggle}
              disabled={disabled}
              data-testid="dropdown-toggle"
              className={`text-gray-400 hover:text-gray-600 focus:outline-none ${
                disabled ? 'cursor-not-allowed' : ''
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Render dropdown content using portal when open */}
        {isOpen && createPortal(
          <div
            style={{
              position: 'absolute',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              zIndex: 99999
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {renderDropdownContent()}
          </div>,
          document.body
        )}
      </div>
    </div>
  )
} 